import React from "react";
import { default as ReactPhoneInput } from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./phoneInput.css";
import { classnames } from '../../../../utils/classnames';
import InputErrors, {withErrors} from "../inputErrors/inputErrors";
import { useTranslation } from 'react-i18next';
import { UUID } from "../../../../utils/utils";
import Input from "../inputs/inputs";


export default function PhoneInput({ value, country, ...rest }) {
	const withDialCode = React.useMemo(() => {
		const dialCodeRegex = /^[(]?[+][0-9]{1,4}[)]?.*/gm;
		if (value) {
			return dialCodeRegex.test(value.trim());
		}
		return true;
	}, [value]);

	const defaultCountry = React.useMemo(() => country?.toLowerCase() || 'fr', [country]);

	return withDialCode && defaultCountry ? (
		<Phone value={value} country={defaultCountry} {...rest}/>
	) : (
		<Input value={value} {...rest}/>
	);
}

/**
 * @function PhoneInput
 * @description Create phone input
 * @param useNew
 * @param {Number} debounceTimeout - DebounceTimeout is the maximum time between two key strokes that won't trigger a change event
 * @param {String} value - value of the input
 * @param errors
 * @param disabled
 * @param className
 * @param rest
 */
export function Phone ({
	uniqueKey = null,
	value,
    label,
    description,
	country = null,
    required,
	debounceTimeout,
	errors = null,
	disabled,
	className,
	display = true,
	onChange = () => {},
	autoComplete = null,
	countryCodeEditable = false,
	...rest
}) {
	const { t } = useTranslation();
	const fallBackUUID = UUID();
	const [pastedString, setPastedString] = React.useState('');

	const getKey = React.useCallback((element = null) => `field_${uniqueKey || fallBackUUID}${element ? '_' + element : ''}`, [uniqueKey]);

	const [idInput, ariaLabelledBy, ariaDescribedBy, idContainer, idSelectFlag, idCountryList, idCountry] = React.useMemo(() => [
		getKey(),
		getKey('label'),
		`${getKey('description')} ${getKey('error')}`,
		getKey('container'),
		getKey('select_flag'),
		getKey('country_list'),
		getKey('country')
	], [getKey]);

	React.useEffect(() => {
		// it's a lib component, not very friendly to overwrite/change behaviours
		if (idContainer) {
			const elementContainer = document?.querySelector(`.neo-input-content#${idContainer}`);
			const phoneContainer = elementContainer?.querySelector('.react-tel-input');
			const inputText = phoneContainer?.getElementsByClassName("form-control neo-input-text neo-input-tel_w_flag");
			const dropdownCountry = phoneContainer?.getElementsByClassName("flag-dropdown neo-input-flag");
			const selectFlag = phoneContainer?.querySelector(".selected-flag");

			const onClick = function(e){
				const open = e?.target?.class?.includes("open");
				selectFlag?.setAttribute("aria-expanded", open);
				// wait for ul to spawn and add ids on ul and active li for accessibility
				setTimeout(() => {
					const ul = selectFlag?.parentElement?.getElementsByTagName("ul");
					if (ul?.length) {
						const updateActive = (e) => {
							if (['ArrowDown', 'ArrowUp'].includes(e.key))
								setTimeout(() => {
									//on keydown update active country after it is highlighted
									const highlightedCountries = ul[0].getElementsByClassName('country highlight');
									ul[0].setAttribute('aria-activedescendant', highlightedCountries[0].id);
								}, 100);
						};

						ul[0].setAttribute('id', idCountryList);
						ul[0].setAttribute('aria-labbeledby', idSelectFlag);
						Array.from(ul[0].children).forEach((country, index) => {
							//set id for each country and get the active one
							country.setAttribute('id', `${idCountry}_${index}`);
							if (country.className.includes('highlight'))
								ul[0].setAttribute('aria-activedescendant', `${idCountry}_${index}`);
						});
						ul[0].addEventListener("keydown", updateActive);
					}
				}, 10);
			};
			// switch html order of dropdown and input for tab navigation
			if (inputText?.length && dropdownCountry?.length) {
				dropdownCountry[0].setAttribute("tabindex", "-1");
				dropdownCountry[0].onkeydown=(e) => {if(e.key === 'Enter') onClick(e)}
				phoneContainer.appendChild(inputText[0]);
			}
			if (selectFlag) {
				selectFlag.setAttribute("id", idSelectFlag);
				selectFlag.setAttribute("aria-labelledby", ariaLabelledBy);
				selectFlag.setAttribute("aria-describedby", `${ariaDescribedBy} neo-country-phone-screenreader`);
				selectFlag.setAttribute("aria-controls", idCountryList);
				selectFlag.setAttribute("aria-expanded", false);
				selectFlag.onkeydown=(e) => {if([' '].includes(e.key)) selectFlag.click(e)};
				selectFlag.onclick=(e) => onClick(e);
			}

			if (inputText?.length) {
				inputText[0].setAttribute("id", idInput);
				inputText[0].setAttribute("autocomplete", autoComplete);
				inputText[0].setAttribute("tabindex", "0");
				inputText[0].setAttribute("aria-hidden", "false");
				inputText[0].setAttribute("aria-labelledby", ariaLabelledBy);
				inputText[0].setAttribute("aria-describedby", ariaDescribedBy);
				// prevent unfocus (voice over reading only "unselected")
				inputText[0].onfocus = function(e) {
					e.currentTarget.selectionStart = e.currentTarget.selectionEnd;
				};
				// prevent pasting numbers starting with 0
				inputText[0].onpaste = function(e) {
					var pastedText = undefined;
  					if (window.clipboardData && window.clipboardData.getData) {
  					  pastedText = window.clipboardData.getData('Text');
  					} else if (e.clipboardData && e.clipboardData.getData) {
  					  pastedText = e.clipboardData.getData('text/plain');
  					}
					// if starts with 0 prevent it and do a new paste with correct number
					if (pastedText[0] === '0') {
						pastedText = pastedText.slice(1);
						setPastedString(pastedText);
					}
				};
			}
		}
	}, [])

	const _onChange = React.useCallback((phone, country, event, valueFormatted) => {
		const countryMatch = new RegExp(`^(${country.dialCode})[0-9]*`);
		const arr = phone.match(countryMatch);
		// if starts with country code
		if (arr?.length) {
			const phoneLength = phone.length;
			const maxLength = [...country.format].filter(c => c === '.').length;
			// only replace if full number paste
			if (pastedString && phoneLength === maxLength && pastedString.length === (maxLength - country.dialCode.length)) {
				phone = `+${country.dialCode}${pastedString}`;
			} else {
				// get number without country code
				phone = phone.slice(country.dialCode.length);
				// remove starting 0
				if (phone[0] === '0')
					phone = phone.slice(1);
				// add back country code
				phone = `+${country.dialCode}${phone}`;
			}
			if (pastedString)
				setPastedString(null);
			onChange(phone);
		} else {
			onChange(valueFormatted)
		}

		document.querySelector(`input#${idInput}`)?.focus();
	}, [onChange, pastedString, idInput]);

	if (!display) {
		return null
	}

	return (
        <div className={classnames('neo-input-content', className)} id={idContainer}>
            {label && <label className='neo-input-label' id={getKey('label')}>{label} {required && <span aria-label={t('widget.required-field')} className='neo-input-required'>*</span>}</label>}
            {description && <p className='neo-input-description' id={getKey('description')}>{description}</p>}
			<div className="neo-div-aria-live" id="neo-country-phone-screenreader">
				{t('widget.input.phone.country-dropdown')}
			</div>
		    <ReactPhoneInput
		    	containerClass={classnames("neo-phone-input", className, {
		    		"neo-ui-input__errors": withErrors(errors),
		    		"neo-ui-input__disabled": disabled
		    	})}
		    	inputClass={classnames("neo-input-text", "neo-input-tel_w_flag", className, {
					"error": withErrors(errors),
					"disabled": disabled
				})}
		    	buttonClass={`neo-input-flag`}
		    	value={value}
				country={country}
		    	disabled={disabled}
				onChange={_onChange}
				countryCodeEditable={countryCodeEditable}
		    	{...rest}
		    />
			<InputErrors errors={errors} id={getKey('error')}/>
        </div>
	);
}